<template>
  <div class="knowledge-base-article-view">
    <PageSection separator="none" spacing="bottom">
      <div class="top-bar">
        <RouterLink to="/knowledge-base" class="back-link">
          <span class="back-arrow">←</span> Knowledge Base
        </RouterLink>
        <button class="print-button" @click="handlePrint">
          <span class="print-icon">🖨</span> Print
        </button>
      </div>
    </PageSection>

    <PageSection separator="top" spacing="both">
      <ContentWithLoadingSkeleton :isLoading="isLoading">
        <div v-if="article" class="article-layout">
          <div class="article-content">
            <h1 class="article-title">{{ article.title }}</h1>
            <div class="article-meta">
              Modified on: {{ formatDate(article.updated_at || article.modified_at) }}
            </div>
            <div class="article-description" v-html="article.description"></div>
          </div>

          <aside v-if="relatedArticles.length > 0" class="related-articles-sidebar">
            <div class="related-articles-sticky">
              <h2 class="related-articles-title">Related Articles</h2>
              <ul class="related-articles-list">
                <li v-for="relatedArticle in relatedArticles" :key="relatedArticle.id">
                  <RouterLink :to="`/knowledge-base/article/${relatedArticle.id}`" class="related-article-link">
                    {{ relatedArticle.name }}
                  </RouterLink>
                </li>
              </ul>
            </div>
          </aside>
        </div>
        <div v-else-if="!isLoading" class="error-message">
          Article not found
        </div>
      </ContentWithLoadingSkeleton>
    </PageSection>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import PageSection from '@/components/layouts/PageSection.vue';
import ContentWithLoadingSkeleton from '@/components/ContentLoadingSkeleton/ContentWithLoadingSkeleton.vue';
import { fetchArticleById, fetchRelatedArticles } from '@/helpers/api/knowledgeBase/freshdeskKnowledgeBase';
import type { Article } from '@/helpers/api/knowledgeBase/types';

const route = useRoute();

const article = ref<Article | null>(null);
const relatedArticles = ref<Array<{ id: number; name: string }>>([]);
const isLoading = ref(false);

function formatDate(dateString: string | undefined): string {
  if (!dateString) return 'Unknown';

  try {
    const date = new Date(dateString);
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    const dayName = days[date.getUTCDay()];
    const month = months[date.getUTCMonth()];
    const day = date.getUTCDate();
    const year = date.getUTCFullYear();

    let hours = date.getUTCHours();
    const minutes = date.getUTCMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    const minutesStr = minutes < 10 ? '0' + minutes : minutes;

    return `${dayName}, ${month} ${day}, ${year} at ${hours}:${minutesStr} ${ampm}`;
  } catch (error) {
    return dateString;
  }
}

async function loadArticle() {
  const articleId = route.params.id;
  if (!articleId || typeof articleId !== 'string') {
    return;
  }

  isLoading.value = true;
  try {
    const articleIdNum = parseInt(articleId, 10);
    article.value = await fetchArticleById(articleIdNum);

    // Fetch related articles
    try {
      relatedArticles.value = await fetchRelatedArticles(articleIdNum);
    } catch (error) {
      console.error('Failed to load related articles:', error);
      relatedArticles.value = [];
    }
  } catch (error) {
    console.error('Failed to load article:', error);
    article.value = null;
  } finally {
    isLoading.value = false;
  }
}

function handlePrint() {
  if (!article.value) return;

  const printWindow = window.open('', '', 'width=800,height=600');
  if (!printWindow) return;

  const printContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>${article.value.title}</title>
      <style>
        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
          line-height: 1.6;
          color: #25294A;
          max-width: 900px;
          margin: 20px auto;
          padding: 20px;
        }
        h1 {
          font-size: 2.5em;
          font-weight: bold;
          color: #25294A;
          margin-bottom: 1rem;
          letter-spacing: 1px;
        }
        .article-meta {
          color: #666;
          font-size: 0.9em;
          margin-bottom: 2rem;
          padding-bottom: 1rem;
          border-bottom: 1px solid #e6e6e6;
        }
        img {
          max-width: 100%;
          height: auto;
          margin: 1rem 0;
        }
        p {
          margin: 1rem 0;
        }
        ul, ol {
          margin: 1rem 0;
          padding-left: 2rem;
        }
        li {
          margin: 0.5rem 0;
        }
        h2, h3, h4, h5, h6 {
          margin: 1.5rem 0 1rem 0;
          color: #25294A;
          font-weight: bold;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin: 1rem 0;
        }
        th, td {
          border: 1px solid #e6e6e6;
          padding: 0.75rem;
          text-align: left;
        }
        th {
          background-color: #f5f5f5;
          font-weight: bold;
        }
        @media print {
          body {
            margin: 0;
            padding: 20px;
          }
          h1 {
            page-break-after: avoid;
          }
          img {
            page-break-inside: avoid;
          }
        }
      </style>
    </head>
    <body>
      <h1>${article.value.title}</h1>
      <div class="article-meta">
        Modified on: ${formatDate(article.value.updated_at || article.value.modified_at)}
      </div>
      <div class="article-content">
        ${article.value.description}
      </div>
    </body>
    </html>
  `;

  printWindow.document.write(printContent);
  printWindow.document.close();

  printWindow.onload = () => {
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };
}

onMounted(() => {
  loadArticle();
});

// Watch for route parameter changes to reload article when navigating between articles
watch(() => route.params.id, () => {
  loadArticle();
});
</script>

<style lang="scss" scoped>
.knowledge-base-article-view {
  .top-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 1rem;
  }

  .back-link {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #1c69d4;
    text-decoration: none;
    font-size: 1.1rem;
    font-weight: 500;
    transition: color 0.2s ease-in-out;

    &:hover {
      color: #114f75;
      text-decoration: underline;
    }

    .back-arrow {
      font-size: 1.2em;
    }
  }

  .print-button {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    background-color: transparent;
    border: 1px solid #e6e6e6;
    border-radius: 4px;
    color: #25294A;
    cursor: pointer;
    font-size: 1rem;
    transition: all 0.2s ease-in-out;

    &:hover {
      background-color: #f5f5f5;
      border-color: #25294A;
    }

    .print-icon {
      font-size: 1.1em;
    }
  }

  .article-layout {
    display: flex;
    gap: 2rem;
    align-items: flex-start;
  }

  .article-content {
    flex: 1;
    min-width: 0;
    max-width: 900px;
  }

  .article-title {
    font-size: 2.5em;
    font-weight: bold;
    color: #25294A;
    margin-bottom: 1rem;
    text-transform: none;
    letter-spacing: 1px;
  }

  .article-meta {
    color: #666;
    font-size: 0.9em;
    margin-bottom: 2rem;
  }

  .article-description {
    color: #25294A;
    line-height: 1.6;
    font-size: 1rem;

    :deep(img) {
      max-width: 100%;
      height: auto;
      margin: 1rem 0;
    }

    :deep(p) {
      margin: 1rem 0;
    }

    :deep(ul),
    :deep(ol) {
      margin: 1rem 0;
      padding-left: 2rem;
    }

    :deep(li) {
      margin: 0.5rem 0;
    }

    :deep(h1),
    :deep(h2),
    :deep(h3),
    :deep(h4),
    :deep(h5),
    :deep(h6) {
      margin: 1.5rem 0 1rem 0;
      color: #25294A;
      font-weight: bold;
    }

    :deep(table) {
      width: 100%;
      border-collapse: collapse;
      margin: 1rem 0;
    }

    :deep(th),
    :deep(td) {
      border: 1px solid #e6e6e6;
      padding: 0.75rem;
      text-align: left;
    }

    :deep(th) {
      background-color: #f5f5f5;
      font-weight: bold;
    }
  }

  .error-message {
    color: #d32f2f;
    font-size: 1.2rem;
    text-align: center;
    padding: 2rem;
  }

  .related-articles-sidebar {
    flex-shrink: 0;
    width: 300px;
  }

  .related-articles-sticky {
    position: sticky;
    top: 2rem;
    background-color: #f9f9f9;
    border: 1px solid #e6e6e6;
    border-radius: 4px;
    padding: 1.5rem;
  }

  .related-articles-title {
    font-size: 1.2em;
    font-weight: bold;
    color: #25294A;
    margin-bottom: 1rem;
    margin-top: 0;
  }

  .related-articles-list {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  .related-articles-list li {
    margin: 0.75rem 0;
    padding-left: 1.5rem;
    position: relative;

    &::before {
      content: '📄';
      position: absolute;
      left: 0;
      font-size: 0.9em;
    }
  }

  .related-article-link {
    color: #1c69d4;
    text-decoration: none;
    font-size: 0.95rem;
    line-height: 1.5;
    transition: color 0.2s ease-in-out;

    &:hover {
      color: #114f75;
      text-decoration: underline;
    }
  }

  @media (max-width: 1024px) {
    .article-layout {
      flex-direction: column;
    }

    .related-articles-sidebar {
      width: 100%;
      max-width: 600px;
    }

    .related-articles-sticky {
      position: static;
    }
  }
}

@media print {

  // Keep this for fallback, but the new method uses a separate window
  .knowledge-base-article-view {
    .top-bar {
      display: none;
    }
  }
}
</style>
